import React, { useState, useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import api from 'config/axiosConfig';
import { Meeting, Issue, Influence, Solution, Sort, SortField, SortOrder } from 'util/constants';
import { useUser } from 'util/UserContext';
import MeetingCard from './MeetingCard';
import LoadingSkeleton from 'ui/LoadingSkeleton';
import AddMeeting from './AddMeeting';
import ScrollHideContainer from 'ui/ScrollHideContainer';
import { SelectTrigger, SelectValue, SelectItem, SelectContent, Select } from 'ui/InputSelect';
import NotFoundPage from 'components/ResponsePages/404NotFoundPage';
import ServerErrorPage from 'components/ResponsePages/500ServerErrorPage';
import AddButton from 'ui/AddButton';
import { AxiosError } from 'axios';

interface Props {
    sort: Sort;
    entity?: Issue | Influence | Solution;
    entityType?: 'issue' | 'influence' | 'solution' | 'team';
}

const MeetingsSection = ({ sort, entity, entityType }: Props) => {
    const { currentUser, setShowAuthModal } = useUser();
    const [meetings, setMeetings] = useState<Meeting[]>([]);
    const [error, setError] = useState<string>('');
    const [openModal, setOpenModal] = useState<boolean>(false);
    const [isPrivateFilter, setIsPrivateFilter] = useState<'All' | 'public' | 'private'>('All');
    const [timeFilter, setTimeFilter] = useState<'All' | 'upcoming' | 'past'>('upcoming');
    const [sortField, setSortField] = useState<SortField>('start_at');
    const [sortOrder, setSortOrder] = useState<SortOrder>('asc');
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [inViewRef, inView] = useInView({
        triggerOnce: true,
        threshold: 0.1,
    });

    useEffect(() => {
        switch (sort) {
            case 'top':
                setSortField('participant_count');
                setSortOrder('desc');
                break;
            case 'new':
                setSortField('created_at');
                setSortOrder('desc');
                break;
            case 'soonest':
                setSortField('start_at');
                setSortOrder('asc');
                break;
            case 'latest':
                setSortField('start_at');
                setSortOrder('desc');
                break;
            default:
                setSortField('start_at');
                setSortOrder('asc');
                break;
        }
    }, [sort]);

    const fetchMeetings = async () => {
        setIsLoading(true);
        try {
            let endpoint = '/api/meetings';
            
            if (entity && entityType) {
                endpoint = `/api/meetings/entity/${entityType}/${entity.id}`;
            }
            
            const response = await api.get<Meeting[]>(endpoint);
            setMeetings(response.data || []);
        } catch (err) {
            if (err instanceof AxiosError && err.response?.status === 404) {
                setError('404: Failed to load events. Please try again later.');
            } else {
                setError('Failed to load events. Please try again later.');
            }
            setMeetings([]); // Ensure meetings is always an array even on error
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (inView || (entity && entityType)) {
            fetchMeetings();
        }
        //eslint-disable-next-line
    }, [entity, entityType, inView]);

    const handleMeetingAdded = async () => {
        try {
            await fetchMeetings();
        } catch (err) {
            setError('Failed to refresh events. Please reload the page.');
        }
    };

    const handleSortChange = (newSort: Sort) => {
        switch (newSort) {
            case 'top':
                setSortField('participant_count');
                setSortOrder('desc');
                break;
            case 'new':
                setSortField('created_at');
                setSortOrder('desc');
                break;
            case 'soonest':
                setSortField('start_at');
                setSortOrder('asc');
                break;
            case 'latest':
                setSortField('start_at');
                setSortOrder('desc');
                break;
        }
    };

    const handleAddMeetingButtonClick = () => {
        if (!currentUser) {
            setShowAuthModal(true);
        } else {
            setOpenModal(true);
        }
    };

    const handleCloseModal = () => {
        setOpenModal(false);
    };

    // Make sure meetings is always an array before filtering
    const filteredMeetings = (meetings || []).filter((meeting) => {
        // Filter by privacy
        if (isPrivateFilter !== 'All') {
            const isPrivate = meeting?.properties?.is_private || false;
            if (isPrivateFilter === 'private' && !isPrivate) return false;
            if (isPrivateFilter === 'public' && isPrivate) return false;
        }

        // Filter by time
        if (timeFilter !== 'All') {
            const now = new Date();
            const startDate = meeting?.properties?.start_at ? new Date(meeting.properties.start_at) : null;
            
            if (timeFilter === 'upcoming' && startDate && startDate < now) return false;
            if (timeFilter === 'past' && (!startDate || startDate >= now)) return false;
        }

        return true;
    });

    const sortedMeetings = [...filteredMeetings].sort((a, b) => {
        if (!sortField) return 0;

        let aValue, bValue;

        switch (sortField) {
            case 'participant_count':
                aValue = a.participant_count || 0;
                bValue = b.participant_count || 0;
                break;
            case 'created_at':
                aValue = new Date(a.created_at || 0).getTime();
                bValue = new Date(b.created_at || 0).getTime();
                break;
            case 'start_at':
                aValue = a.properties?.start_at ? new Date(a.properties.start_at).getTime() : 0;
                bValue = b.properties?.start_at ? new Date(b.properties.start_at).getTime() : 0;
                break;
            default:
                return 0;
        }

        if (sortOrder === 'asc') {
            return aValue - bValue;
        } else {
            return bValue - aValue;
        }
    });

    if (error) {
        return error.match(/404/) ? 
            <NotFoundPage 
                displayStyle="page" 
                errorMessage="Sorry, we couldn't find any events for this entity." 
                buttonText="View Other Events" 
                buttonHref="/communitea/events" 
            /> : 
            <ServerErrorPage />;
    }

    return (
        <div className="meetings-section" ref={inViewRef}>
            <ScrollHideContainer 
                className="z-40 max-w-5xl bg-eoe-bg dark:bg-eoe-dark-bg border-b border-eoe-border-main dark:border-eoe-dark-border-main"
                hideOffset={115}
                threshold={114}
            >
                <div className="flex flex-col space-y-1 mb-1">
                    <div className="text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub">
                        Under development - may not function properly.
                    </div>
                    <div className="flex mb-1 mx-1">
                        <AddButton
                            tooltipTitle="Add Event"
                            buttonText="Event"
                            className="mx-2 w-full"
                            onClick={handleAddMeetingButtonClick}
                        />
                    </div>
                    <div className="flex mx-3 gap-1 pb-1">
                        <Select onValueChange={(value: Sort) => handleSortChange(value)}>
                            <SelectTrigger>
                                <SelectValue placeholder="Sort" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="soonest">Soonest</SelectItem>
                                <SelectItem value="latest">Latest</SelectItem>
                                <SelectItem value="top">Most Popular</SelectItem>
                                <SelectItem value="new">Newest Added</SelectItem>
                            </SelectContent>
                        </Select>
                        <Select onValueChange={(value) => setTimeFilter(value as 'All' | 'upcoming' | 'past')}>
                            <SelectTrigger>
                                <SelectValue placeholder="Time" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="All">All Events</SelectItem>
                                <SelectItem value="upcoming">Upcoming</SelectItem>
                                <SelectItem value="past">Past</SelectItem>
                            </SelectContent>
                        </Select>
                        <Select onValueChange={(value) => setIsPrivateFilter(value as 'All' | 'public' | 'private')}>
                            <SelectTrigger>
                                <SelectValue placeholder="Privacy" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="All">All Events</SelectItem>
                                <SelectItem value="public">Public Only</SelectItem>
                                <SelectItem value="private">Private Only</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </div>
            </ScrollHideContainer>
            
            <div className="mt-2">
                {isLoading ? (
                    <LoadingSkeleton count={3} />
                ) : meetings.length === 0 ? (
                    <p className="m-4 text-sm">Be the first to add an event{entity ? ' for this ' + entityType : ''}!</p>
                ) : (
                    <div className="flex flex-col space-y-4 px-2">
                        {sortedMeetings.map((meeting) => (
                            <MeetingCard
                                key={meeting.id}
                                meeting={meeting}
                                truncateDescription={true}
                                isMeetingProfilePage={false}
                                isEntitySection={true}
                            />
                        ))}
                    </div>
                )}
            </div>
            
            {openModal && (
                <AddMeeting
                    entity={entity}
                    entityType={entityType}
                    onMeetingAdded={handleMeetingAdded}
                    open={openModal}
                    onClose={handleCloseModal}
                />
            )}
        </div>
    );
};

export default MeetingsSection;
